// src/components/kioskPhotos/KioskPhotos.jsx
//
// /admin/kiosk-photos (admins): the background photos of the kiosks' home
// screen. Add (cropped to 800x480 in the browser, PhotoCropper), choose the
// default, assign a photo to a zone (territory) or to a facility, and check
// which photo a facility's kiosk gets. Backend: routes/kioskPhotos.ts; the
// choice order (facility > zone > parent zone > default): utils/kioskPhoto.ts.
// The kiosk sees the change at its next config call (sha256 of the photo).
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Container, Typography, Box, Button, Paper, Stack, Chip, Alert, CircularProgress, Dialog, DialogTitle,
  DialogContent, DialogActions, Tabs, Tab, TextField, MenuItem, List, ListItemButton, ListItemText,
  FormControlLabel, Switch, Checkbox,
} from '@mui/material';
import useRoleAuth from '../../utils/useRoleAuth';
import {
  listPhotos, uploadPhoto, renamePhoto, setDefaultPhoto, deletePhoto, assignPhoto, listTerritories,
  searchFacilities, resolvePhoto, photoErrorText, getKioskSettings, setWeatherDefault, setDemoFacilities,
} from '../../utils/kioskPhotoApi';
import PhotoCropper from './PhotoCropper';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8080';
const imageUrl = (sha256) => `${API_BASE_URL}/public/kiosk-photo/${sha256}.jpg`;
const SOURCE = { FACILITY: 'photo de l’établissement', TERRITORY: 'photo de la zone', DEFAULT: 'photo par défaut' };

/** Territories as "Paris › Paris 11", sorted by that path. */
function territoryPaths(territories) {
  const byId = new Map(territories.map((t) => [t.id, t]));
  const path = (t) => {
    const names = [];
    const seen = new Set();
    for (let c = t; c && !seen.has(c.id); c = c.parentId != null ? byId.get(c.parentId) : null) { seen.add(c.id); names.unshift(c.name); }
    return names.join(' › ');
  };
  return territories.map((t) => ({ ...t, path: path(t) })).sort((a, b) => a.path.localeCompare(b.path, 'fr'));
}

function AssignDialog({ photo, getToken, onClose, onAssigned }) {
  const [tab, setTab] = useState(0);
  const [territories, setTerritories] = useState([]);
  const [territoryId, setTerritoryId] = useState('');
  const [q, setQ] = useState('');
  const [facilities, setFacilities] = useState([]);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!photo) return;
    listTerritories(getToken()).then((r) => setTerritories(territoryPaths(r.territories))).catch((e) => setError(photoErrorText(e)));
  }, [photo, getToken]);
  useEffect(() => {
    if (!q.trim()) { setFacilities([]); return undefined; }
    const t = setTimeout(() => searchFacilities(q.trim(), getToken()).then((r) => setFacilities(r.facilities)).catch(() => {}), 250);
    return () => clearTimeout(t);
  }, [q, getToken]);

  const assign = async (targetType, targetId) => {
    setError('');
    try { await assignPhoto(targetType, targetId, photo.id, getToken()); onAssigned(); } catch (e) { setError(photoErrorText(e)); }
  };

  return (
    <Dialog open={Boolean(photo)} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Assigner « {photo?.name} »</DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Tabs value={tab} onChange={(_e, v) => setTab(v)} sx={{ mb: 2 }}>
          <Tab label="À une zone" />
          <Tab label="À un établissement" />
        </Tabs>
        {tab === 0 && (
          <Stack spacing={2}>
            <TextField select fullWidth size="small" label="Zone de prospection" value={territoryId} onChange={(e) => setTerritoryId(e.target.value)}>
              {territories.map((t) => (
                <MenuItem key={t.id} value={t.id}>{t.path}{t.active ? '' : ' (inactive)'}</MenuItem>
              ))}
            </TextField>
            <Typography variant="body2" color="text.secondary">
              Couvre aussi les sous-zones qui n’ont pas leur propre photo, sauf les établissements qui ont la leur.
            </Typography>
            <Button variant="contained" disabled={!territoryId} onClick={() => assign('TERRITORY', territoryId)}>Assigner à cette zone</Button>
          </Stack>
        )}
        {tab === 1 && (
          <>
            <TextField fullWidth size="small" label="Nom ou numéro de l’établissement" value={q} onChange={(e) => setQ(e.target.value)} autoFocus />
            <List dense>
              {facilities.map((f) => (
                <ListItemButton key={f.id} onClick={() => assign('FACILITY', f.id)}>
                  <ListItemText primary={`${f.name} (#${f.id})`} secondary={f.address || null} />
                </ListItemButton>
              ))}
            </List>
          </>
        )}
      </DialogContent>
      <DialogActions><Button onClick={onClose}>Fermer</Button></DialogActions>
    </Dialog>
  );
}

// Weather on the home screen (icon, temperature, « Pluie dans 15 min »): the
// global setting; each facility can follow it or force it on / off on the
// Manager board. Backend utils/kioskSettings.ts, utils/weather.ts.
function WeatherSetting({ getToken }) {
  const [on, setOn] = useState(null);
  const [error, setError] = useState('');
  useEffect(() => {
    getKioskSettings(getToken()).then((r) => setOn(r.weatherDefault)).catch(() => setError('Réglage illisible.'));
  }, [getToken]);
  const toggle = async (value) => {
    setError(''); setOn(value);
    try { await setWeatherDefault(value, getToken()); } catch { setOn(!value); setError('Impossible d’enregistrer ce réglage.'); }
  };
  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Météo sur l’écran d’accueil</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1, maxWidth: 760 }}>
        En haut à droite de la photo : le temps qu’il fait et la température. Si la pluie arrive dans l’heure, un bandeau
        « Pluie dans 15 min — restez au sec, notez-nous ! ». Les bornes la reçoivent avec leur configuration (aucune
        requête en plus). Chaque établissement peut suivre ce réglage ou le forcer sur le Manager board.
      </Typography>
      {on !== null && (
        <FormControlLabel control={<Switch checked={on} onChange={(e) => toggle(e.target.checked)} />}
          label={on ? 'Activée par défaut' : 'Désactivée par défaut'} />
      )}
      {error && <Alert severity="warning" sx={{ mt: 1 }}>{error}</Alert>}
    </Paper>
  );
}

// Kiosks not associated yet show and rate this test facility (Manager board « Test: ON »):
// ambassadors demo the kiosk at a prospect's with no setup. Backend routes/DeviceClaim.ts.
// Up to 3 test facilities, in the order ticked: the kiosk shows them in that order.
const MAX_DEMO = 3;

export function DemoFacilitySetting({ getToken }) {
  const [settings, setSettings] = useState(null);
  const [error, setError] = useState('');
  useEffect(() => {
    getKioskSettings(getToken())
      .then((r) => setSettings({ demoFacilityIds: r.demoFacilityIds ?? [], testFacilities: r.testFacilities ?? [] }))
      .catch(() => setError('Réglage illisible.'));
  }, [getToken]);
  const toggle = async (id, on) => {
    const before = settings.demoFacilityIds;
    const ids = on ? [...before, id] : before.filter((x) => x !== id);
    setError(''); setSettings((s) => ({ ...s, demoFacilityIds: ids }));
    try { await setDemoFacilities(ids, getToken()); } catch {
      setSettings((s) => ({ ...s, demoFacilityIds: before })); setError('Impossible d’enregistrer ce réglage.');
    }
  };
  const ids = settings?.demoFacilityIds ?? [];
  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Démo sur les bornes non associées</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, maxWidth: 760 }}>
        Une borne pas encore associée propose ces établissements de test (jusqu’à {MAX_DEMO}, dans l’ordre coché) : un
        toucher sur l’un montre son QR de notation, un appui long le code d’association. Les notes de test sont acceptées
        partout, restent hors de la carte, des alertes et des rapports, et leurs statistiques sont ouvertes aux ambassadeurs.
        Passez un établissement en « Test » sur le Manager board pour le proposer ici.
      </Typography>
      {settings && settings.testFacilities.map((f) => {
        const rank = ids.indexOf(f.id);
        return (
          <FormControlLabel
            key={f.id}
            sx={{ display: 'flex' }}
            control={(
              <Checkbox
                checked={rank >= 0}
                disabled={rank < 0 && ids.length >= MAX_DEMO}
                onChange={(e) => toggle(f.id, e.target.checked)}
              />
            )}
            label={`${rank >= 0 ? `${rank + 1}. ` : ''}${f.name} (${f.id})`}
          />
        );
      })}
      {settings && settings.testFacilities.length > 0 && !ids.length && (
        <Typography variant="body2" color="text.secondary">Aucun coché : les bornes affichent leur code d’association.</Typography>
      )}
      {settings && !settings.testFacilities.length && (
        <Alert severity="info" sx={{ mt: 1.5 }}>Aucun établissement en mode test pour l’instant.</Alert>
      )}
      {error && <Alert severity="warning" sx={{ mt: 1 }}>{error}</Alert>}
    </Paper>
  );
}

function WhichPhoto({ getToken }) {
  const [id, setId] = useState('');
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const check = async () => {
    setError(''); setResult(null);
    try { setResult(await resolvePhoto(Number(id), getToken())); } catch (e) { setError(photoErrorText(e)); }
  };
  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>Quelle photo pour un établissement ?</Typography>
      <Stack direction="row" spacing={1}>
        <TextField size="small" label="Numéro" value={id} onChange={(e) => setId(e.target.value.replace(/\D/g, ''))} />
        <Button variant="outlined" disabled={!id} onClick={check}>Vérifier</Button>
      </Stack>
      {error && <Alert severity="warning" sx={{ mt: 1 }}>{error}</Alert>}
      {result && (
        <Box sx={{ mt: 1.5 }}>
          {result.photo ? (
            <Stack direction="row" spacing={2} alignItems="center">
              <img src={imageUrl(result.photo.sha256)} alt={result.photo.name} width={160} height={96} style={{ objectFit: 'cover', borderRadius: 4 }} />
              <Typography variant="body2">
                <b>{result.facility.name}</b> affiche « {result.photo.name} » — {SOURCE[result.photo.source]}
                {result.photo.territoryName ? ` « ${result.photo.territoryName} »` : ''}.
              </Typography>
            </Stack>
          ) : (
            <Typography variant="body2"><b>{result.facility.name}</b> n’a pas de photo : la borne garde son écran sans photo.</Typography>
          )}
        </Box>
      )}
    </Paper>
  );
}

export default function KioskPhotos() {
  const { ready, forbidden, loading, getToken } = useRoleAuth(['ROLE_ADMIN']);
  const [photos, setPhotos] = useState(null);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [file, setFile] = useState(null);
  const [assigning, setAssigning] = useState(null);

  const load = useCallback(() => listPhotos(getToken()).then((r) => setPhotos(r.photos)).catch((e) => setError(photoErrorText(e))), [getToken]);
  useEffect(() => { if (ready) load(); }, [ready, load]);

  const run = async (fn, done) => {
    setError(''); setInfo('');
    try { await fn(); if (done) setInfo(done); await load(); } catch (e) { setError(photoErrorText(e)); }
  };
  const onUploaded = async (blob, name) => {
    const r = await uploadPhoto(blob, name, getToken()).catch((e) => { throw new Error(photoErrorText(e)); });
    setFile(null);
    setInfo(r.existed ? 'Cette photo est déjà dans la bibliothèque.' : `« ${name} » ajoutée.`);
    await load();
  };
  const remove = (p) => {
    const what = p.assignments.map((a) => a.name).join(', ');
    const msg = `Supprimer « ${p.name} » ?` + (p.isDefault ? ' C’est la photo par défaut.' : '')
      + (what ? ` Elle ne sera plus affichée pour : ${what}.` : '');
    if (!window.confirm(msg)) return;
    run(() => deletePhoto(p.id, getToken()), `« ${p.name} » supprimée.`);
  };
  const rename = (p) => {
    const name = window.prompt('Nouveau nom', p.name);
    if (name && name.trim() && name.trim() !== p.name) run(() => renamePhoto(p.id, name.trim(), getToken()));
  };
  const unassign = (a) => run(() => assignPhoto(a.targetType, a.targetId, null, getToken()), `Photo retirée de ${a.name}.`);
  const hasDefault = useMemo(() => photos?.some((p) => p.isDefault), [photos]);

  if (loading) return <Box sx={{ p: 6, textAlign: 'center' }}><CircularProgress /></Box>;
  if (forbidden) return <Container sx={{ py: 4 }}><Alert severity="error">Réservé aux administrateurs.</Alert></Container>;

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" useFlexGap spacing={1} sx={{ mb: 1 }}>
        <Typography variant="h4" sx={{ fontWeight: 700 }}>Photos et réglages des bornes</Typography>
        <Stack direction="row" spacing={1}>
          <Button component={RouterLink} to="/ManagerDashboard">Manager board</Button>
          <Button variant="contained" component="label">
            Ajouter une photo
            <input hidden type="file" accept="image/*" onChange={(e) => { setFile(e.target.files?.[0] || null); e.target.value = ''; }} />
          </Button>
        </Stack>
      </Stack>
      <Typography color="text.secondary" sx={{ mb: 3, maxWidth: 760 }}>
        Fond de l’écran d’accueil des bornes (800×480). Une borne affiche la photo de son établissement, sinon celle
        de sa zone (ou d’une zone parente), sinon la photo par défaut. Elle voit le changement à son prochain contact
        avec le serveur.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {info && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setInfo('')}>{info}</Alert>}
      {photos && photos.length > 0 && !hasDefault && (
        <Alert severity="info" sx={{ mb: 2 }}>Aucune photo par défaut : les bornes sans photo de zone ni d’établissement gardent leur écran actuel.</Alert>
      )}

      <WeatherSetting getToken={getToken} />
      <DemoFacilitySetting getToken={getToken} />
      <WhichPhoto getToken={getToken} />

      {!photos && <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>}
      {photos && photos.length === 0 && <Typography color="text.secondary">Aucune photo pour l’instant : « Ajouter une photo ».</Typography>}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 2 }}>
        {photos?.map((p) => (
          <Paper key={p.id} variant="outlined" sx={{ overflow: 'hidden' }}>
            <img src={imageUrl(p.sha256)} alt={p.name} style={{ width: '100%', aspectRatio: '800 / 480', objectFit: 'cover', display: 'block' }} />
            <Box sx={{ p: 1.5 }}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                <Typography sx={{ fontWeight: 700, flex: 1, minWidth: 0 }} noWrap>{p.name}</Typography>
                {p.isDefault && <Chip size="small" color="primary" label="Par défaut" />}
              </Stack>
              <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
                {p.assignments.length === 0 && <Typography variant="caption" color="text.secondary">Assignée à aucune zone ni établissement.</Typography>}
                {p.assignments.map((a) => (
                  <Chip key={`${a.targetType}-${a.targetId}`} size="small" variant="outlined"
                    label={a.targetType === 'TERRITORY' ? `Zone : ${a.name}` : `${a.name} (#${a.targetId})`}
                    onDelete={() => unassign(a)} />
                ))}
              </Stack>
              <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                <Button size="small" onClick={() => setAssigning(p)}>Assigner…</Button>
                {!p.isDefault && <Button size="small" onClick={() => run(() => setDefaultPhoto(p.id, getToken()), `« ${p.name} » est la photo par défaut.`)}>Mettre par défaut</Button>}
                <Button size="small" onClick={() => rename(p)}>Renommer</Button>
                <Button size="small" color="error" onClick={() => remove(p)}>Supprimer</Button>
              </Stack>
            </Box>
          </Paper>
        ))}
      </Box>

      <PhotoCropper file={file} onCancel={() => setFile(null)} onDone={onUploaded} />
      <AssignDialog photo={assigning} getToken={getToken} onClose={() => setAssigning(null)}
        onAssigned={() => { const p = assigning; setAssigning(null); setInfo(`« ${p.name} » assignée.`); load(); }} />
    </Container>
  );
}
